/**
 * A small clickable music toggle in the HUD.
 *
 * Displays a speaker icon (♪ / ♪̶) that the player can click to
 * mute or unmute the game background music. Keyboard shortcut: M.
 *
 * Emits Events.MUSIC_TOGGLED on the shared event bus when toggled.
 */

import { Events } from '../utils/constants.js';

const STYLE_ON = {
  fontFamily: '"Courier New", Courier, monospace',
  fontSize: '16px',
  color: '#ffffff',
};

const STYLE_OFF = {
  fontFamily: '"Courier New", Courier, monospace',
  fontSize: '16px',
  color: '#555555',
};

const ICON_ON = '\u266A';     // ♪
const ICON_OFF = '\u266A';    // ♪ (will be dimmed via style)

export default class MusicToggle {
  /**
   * @param {Phaser.Scene} scene
   * @param {number} x
   * @param {number} y
   * @param {boolean} initialState - Whether music is initially on.
   * @param {Phaser.Events.EventEmitter} eventBus
   */
  constructor(scene, x, y, initialState, eventBus) {
    this.scene = scene;
    this.eventBus = eventBus;
    this.enabled = initialState;

    // Music icon text
    this.icon = scene.add.text(x, y, '', STYLE_ON);
    this.icon.setOrigin(0.5, 0.5);
    this.icon.setDepth(210);

    // Strikethrough line (drawn when music is off)
    this.strikeGraphics = scene.add.graphics();
    this.strikeGraphics.setDepth(211);

    this._updateDisplay();

    // Make it interactive (clickable)
    this.icon.setInteractive({ useHandCursor: true });
    this.icon.on('pointerdown', () => {
      this.toggle();
    });

    // Hover effect
    this.icon.on('pointerover', () => {
      this.icon.setAlpha(0.7);
    });
    this.icon.on('pointerout', () => {
      this.icon.setAlpha(1);
    });

    // Keyboard shortcut: M key
    this._mKey = scene.input.keyboard.addKey('M');
    this._mKey.on('down', () => {
      this.toggle();
    });
  }

  /**
   * Toggle the music on/off state and emit the event.
   */
  toggle() {
    this.enabled = !this.enabled;
    this._updateDisplay();
    this.eventBus.emit(Events.MUSIC_TOGGLED, this.enabled);
  }

  /**
   * Update the visual display to match the current state.
   * @private
   */
  _updateDisplay() {
    if (this.enabled) {
      this.icon.setText(ICON_ON);
      this.icon.setStyle(STYLE_ON);
    } else {
      this.icon.setText(ICON_OFF);
      this.icon.setStyle(STYLE_OFF);
    }
    this._drawStrike();
  }

  /**
   * Draw or clear the strikethrough line over the icon.
   * @private
   */
  _drawStrike() {
    this.strikeGraphics.clear();
    if (!this.enabled) {
      const bounds = this.icon.getBounds();
      this.strikeGraphics.lineStyle(1.5, 0xff0000, 0.7);
      this.strikeGraphics.lineBetween(
        bounds.x - 1,
        bounds.y + bounds.height - 2,
        bounds.x + bounds.width + 1,
        bounds.y + 2,
      );
    }
  }

  /** Clean up. */
  destroy() {
    if (this.icon) {
      this.icon.destroy();
      this.icon = null;
    }
    if (this.strikeGraphics) {
      this.strikeGraphics.destroy();
      this.strikeGraphics = null;
    }
    if (this._mKey) {
      this._mKey.removeAllListeners();
      if (this.scene && this.scene.input && this.scene.input.keyboard) {
        this.scene.input.keyboard.removeKey(this._mKey);
      }
      this._mKey = null;
    }
  }
}
